import { useCallback, useEffect, useState } from "react";
import { Shield, ShoppingCart, Flame, Sparkles } from "lucide-react";
import api from "../lib/api";

/**
 * ShieldStore — Streak Insurance Store.
 *
 * Renders a 3-tier card grid where pilots can buy extra Streak Shield
 * charges via Stripe. Charges land in `user_achievements.stats.streak_shields`
 * and auto-deploy on missed daily-run days to preserve the streak.
 *
 * Pricing tiers (server-side source of truth — `SHIELD_BUNDLES` in server.py):
 *  - Single Shield: 1 charge / $0.99
 *  - Triple Shield Pack: 3 charges / $1.99
 *  - Ultra Insurance: 10 charges / $4.99
 */
export default function ShieldStore() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null); // bundle_id currently checking out

  const refresh = useCallback(() => {
    api.get("/shields/store").then((r) => setData(r.data)).catch(() => setData(null));
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const buy = async (bundleId) => {
    if (busy) return;
    setBusy(bundleId);
    try {
      const r = await api.post("/shields/checkout", {
        bundle_id: bundleId,
        origin_url: window.location.origin,
      });
      if (r.data?.url) {
        window.location.href = r.data.url;
      }
    } catch (_) {
      setBusy(null);
    }
  };

  if (!data) return null;

  const accentByBundle = {
    single: "#00ffff",
    triple: "#ffd700",
    ultra:  "#b026ff",
  };

  return (
    <div
      id="shield-store"
      className="mb-6 glass p-5 border border-[#b026ff]/40 scroll-mt-24"
      data-testid="shield-store"
      style={{ boxShadow: "0 0 18px rgba(176,38,255,0.18)" }}
    >
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <Shield className="w-5 h-5 text-[#b026ff]" />
        <div>
          <div className="font-mono-cosmic text-[10px] tracking-[0.35em] text-[#b026ff] uppercase">
            // STREAK INSURANCE
          </div>
          <div className="font-heading uppercase tracking-tight text-lg sm:text-xl text-white">
            Shield Store
          </div>
        </div>
        <div className="ml-auto flex items-center gap-2 flex-wrap">
          <div
            className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-[#b026ff]/60 bg-[#b026ff]/5 text-[#b026ff] font-mono-cosmic text-[10px] tracking-[0.3em] uppercase"
            data-testid="shield-store-current"
          >
            <Shield className="w-3.5 h-3.5" />
            {data.current_shields} Active
          </div>
          {data.daily_streak > 0 && (
            <div className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-[#ff9500]/60 bg-[#ff9500]/5 text-[#ff9500] font-mono-cosmic text-[10px] tracking-[0.3em] uppercase">
              <Flame className="w-3.5 h-3.5" />
              {data.daily_streak.toLocaleString()}-Day Streak
            </div>
          )}
        </div>
      </div>

      <p className="text-white/60 text-sm mb-4">
        Auto-deploys on a missed daily-run day to preserve your streak. Stack as many as you need —
        more shields = better insurance against busy days.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {data.bundles.map((b) => {
          const accent = accentByBundle[b.id] || "#00ffff";
          const isBusy = busy === b.id;
          return (
            <button
              key={b.id}
              type="button"
              onClick={() => buy(b.id)}
              disabled={!!busy}
              className={`group text-left border bg-black/30 p-4 transition flex flex-col gap-2 disabled:cursor-not-allowed disabled:opacity-60`}
              style={{
                borderColor: `${accent}66`,
                boxShadow: isBusy ? `0 0 14px ${accent}55` : "none",
              }}
              data-testid={`shield-bundle-${b.id}`}
            >
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4" style={{ color: accent }} />
                <div className="font-heading uppercase tracking-tight text-base text-white group-hover:text-white">
                  {b.label}
                </div>
                {b.id === "ultra" && (
                  <Sparkles className="w-3.5 h-3.5 text-[#ffd700] ml-auto" />
                )}
              </div>
              <div className="font-mono-cosmic text-[10px] tracking-[0.3em] uppercase text-white/50">
                {b.quantity} {b.quantity === 1 ? "charge" : "charges"}
                {b.id === "triple" && (
                  <span className="text-[#00ff41] ml-2">· -33%</span>
                )}
                {b.id === "ultra" && (
                  <span className="text-[#00ff41] ml-2">· -50%</span>
                )}
              </div>
              <div className="flex items-end justify-between mt-1">
                <span
                  className="font-heading font-black text-2xl"
                  style={{ color: accent }}
                >
                  ${b.price_usd.toFixed(2)}
                </span>
                <span className="inline-flex items-center gap-1 px-2 py-1 border text-[10px] tracking-[0.25em] uppercase font-mono-cosmic"
                  style={{ borderColor: accent, color: accent }}>
                  <ShoppingCart className="w-3 h-3" />
                  {isBusy ? "Opening…" : "Buy"}
                </span>
              </div>
            </button>
          );
        })}
      </div>

      <div className="mt-3 font-mono-cosmic text-[9px] tracking-[0.3em] uppercase text-white/40">
        Stripe-secured · charges land instantly after payment confirms.
      </div>
    </div>
  );
}
